import { useState } from 'react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Données des postes par section
const POSTES_BTP = [
  {
    section: 'Ouvriers généraux',
    defaultOpen: true,
    postes: ['Ouvrier', 'Manœuvre', 'Compagnon'],
  },
  {
    section: 'Encadrement terrain',
    defaultOpen: false,
    postes: ['Chef d\'équipe', 'Chef de chantier', 'Conducteur de travaux'],
  },
  {
    section: 'Second œuvre',
    defaultOpen: false,
    postes: ['Carreleur', 'Plaquiste', 'Peintre', 'Plombier', 'Électricien', 'Chauffagiste', 'Menuisier', 'Solier-moquettiste', 'Vitrier'],
  },
  {
    section: 'Gros œuvre',
    defaultOpen: false,
    postes: ['Maçon', 'Coffreur', 'Ferrailleur', 'Bancheur'],
  },
  {
    section: 'Apprentis / Formation',
    defaultOpen: false,
    postes: ['Apprenti', 'Apprenti CAP', 'Apprenti BP', 'Apprenti BTS', 'Stagiaire', 'Alternant'],
  },
  {
    section: 'Toiture / Extérieur',
    defaultOpen: false,
    postes: ['Charpentier', 'Couvreur', 'Étancheur', 'Façadier', 'Zingueur'],
  },
  {
    section: 'VRD / Terrassement',
    defaultOpen: false,
    postes: ['Terrassier', 'Canalisateur', 'Paveur'],
  },
  {
    section: 'Conduite d\'engins',
    defaultOpen: false,
    postes: ['Grutier', 'Conducteur d\'engins'],
  },
  {
    section: 'Métallerie',
    defaultOpen: false,
    postes: ['Serrurier-métallier', 'Soudeur'],
  },
];

interface PosteSelectorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function PosteSelector({ value, onChange, placeholder = 'Poste' }: PosteSelectorProps) {
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    POSTES_BTP.reduce((acc, section) => {
      acc[section.section] = section.defaultOpen;
      return acc;
    }, {} as Record<string, boolean>)
  );
  const [customInput, setCustomInput] = useState('');

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => {
      const isCurrentlyOpen = prev[sectionName];
      // Fermer toutes les sections, puis ouvrir celle cliquée (si elle était fermée)
      const allClosed = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      if (!isCurrentlyOpen) {
        allClosed[sectionName] = true;
      }
      return allClosed;
    });
  };

  const handleSelect = (poste: string) => {
    onChange(poste);
    setOpen(false);
    setCustomInput('');
  };

  const handleCustomSubmit = () => {
    if (customInput.trim()) {
      onChange(customInput.trim());
      setOpen(false);
      setCustomInput('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn(!value && 'text-muted-foreground')}>
            {value || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        {/* Input pour saisie libre */}
        <div className="p-2 border-b">
          <div className="flex gap-2">
            <Input
              placeholder="Saisir un poste..."
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCustomSubmit();
                }
              }}
              className="h-8"
            />
            {customInput && (
              <Button size="sm" onClick={handleCustomSubmit} className="h-8 px-2">
                OK
              </Button>
            )}
          </div>
        </div>

        {/* Sections collapsibles */}
        <div
          className="max-h-[300px] overflow-y-auto overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
            if (scrollKeys.includes(e.key)) {
              e.stopPropagation();
            }
          }}
        >
          {POSTES_BTP.map((section) => (
            <div key={section.section}>
              {/* Header section */}
              <button
                type="button"
                onClick={() => toggleSection(section.section)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
              >
                {expandedSections[section.section] ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {section.section}
                <span className="text-xs text-muted-foreground ml-auto">
                  ({section.postes.length})
                </span>
              </button>

              {/* Postes de la section */}
              {expandedSections[section.section] && (
                <div className="py-1">
                  {section.postes.map((poste) => (
                    <button
                      key={poste}
                      type="button"
                      onClick={() => handleSelect(poste)}
                      className={cn(
                        'w-full flex items-center gap-2 px-4 py-1.5 text-sm hover:bg-accent transition-colors',
                        value === poste && 'bg-accent'
                      )}
                    >
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === poste ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {poste}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
